import { forwardRef, memo, useCallback } from 'react';
import { Text, theme } from '@frameio/vapor';
import {
  IconChatBubble,
  IconCustomFieldAssignee,
  IconCustomFieldCalendar,
  IconCustomFieldSelect,
  IconCustomFieldSelectMulti,
  IconImage,
  IconTranscriptionDefault,
} from '@frameio/vapor-icons';
import type {
  OnSearchSuggestionClick,
  SearchQueryHighlight,
} from '../../../../../types';
import type { SuggestionType } from '../types';
import styles from './SuggestionCard.module.css';

const IconMap: Record<SuggestionType, typeof IconCustomFieldSelect> = {
  status: IconCustomFieldSelect,
  comments: IconChatBubble,
  visuals: IconImage,
  format: IconCustomFieldSelect,
  transcription: IconTranscriptionDefault,
  date: IconCustomFieldCalendar,
  keyword: IconCustomFieldSelectMulti,
  uploader: IconCustomFieldAssignee,
  resolution: IconCustomFieldSelect,
  duration: IconCustomFieldSelect,
  fileSize: IconCustomFieldSelect,
  assignee: IconCustomFieldAssignee,
  fileType: IconCustomFieldSelect,
};

type SuggestionCardProps = {
  description: string;
  title: string;
  type: SuggestionType;
  onClick: OnSearchSuggestionClick;
  tabIndex?: number;
  onFocus?: () => void;
};

/**
 * Parses the query and returns the query and highlight positions using the position of the brackets
 * in the description.
 */
const parseQuery = (
  text: string
): {
  query: string;
  highlight: SearchQueryHighlight | null;
} => {
  const hasBrackets = text.includes('[');

  if (!hasBrackets) {
    return {
      query: text,
      highlight: null,
    };
  }

  const openBracketIndex = text.indexOf('[');
  const closeBracketIndex = text.indexOf(']');

  const beforeBracket = text.substring(0, openBracketIndex);

  const bracketedText = text.substring(openBracketIndex + 1, closeBracketIndex);
  const afterBracket = text.substring(closeBracketIndex + 1);

  const query = beforeBracket + bracketedText + afterBracket;

  return {
    query,
    highlight: {
      starts: beforeBracket.length,
      ends: beforeBracket.length + bracketedText.length,
    },
  };
};

/**
 * Formats the description to enable styling the content within the brackets.
 */
const formatDescriptionWithBrackets = (description: string) => {
  const openBracketIndex = description.indexOf('[');
  const closeBracketIndex = description.indexOf(']');

  if (openBracketIndex === -1 || closeBracketIndex === -1) {
    return description;
  }

  const beforeBracket = description.substring(0, openBracketIndex);
  const bracketedContent = description.substring(
    openBracketIndex,
    closeBracketIndex + 1
  );
  const afterBracket = description.substring(closeBracketIndex + 1);

  return (
    <>
      {beforeBracket}
      <span className={styles.bracketedContent}>{bracketedContent}</span>
      {afterBracket}
    </>
  );
};

export const SuggestionCard = memo(
  forwardRef<HTMLDivElement, SuggestionCardProps>(
    ({ description, title, type, onClick, tabIndex, onFocus }, ref) => {
      const handleOnClick = useCallback(() => {
        const { query, highlight } = parseQuery(description);
        onClick(query, highlight);
      }, [description, onClick]);

      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOnClick();
          }
        },
        [handleOnClick]
      );

      const Icon = IconMap[type];

      return (
        <div
          ref={ref}
          aria-label={description}
          className={styles.suggestionCard}
          onClick={handleOnClick}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={tabIndex}
        >
          <div className={styles.titleContainer}>
            {Icon && <Icon color={theme.colors.icon.secondary} size={16} />}
            <Text color="secondary" variant="secondary">
              {title}
            </Text>
          </div>
          <div>
            <Text className={styles.description} variant="body">
              {formatDescriptionWithBrackets(description)}
            </Text>
          </div>
        </div>
      );
    }
  )
);
